/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosTable, CosmosText } from "@components/elements/atoms";
import { useOsName } from "@components/hooks";
import { CosmosPluginDownloadFragment } from "@cosmos/hygraph-schema";
import { Toggle } from "@eds-web/react/Toggle";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArchivedVersionTableItem, ArchivedVersionTableItemProps } from "./ArchivedVersionTable.item";

export interface ArchivedVersionTableProps {
	items?: CosmosPluginDownloadFragment[];
	overrideDownloadUrl?: string | null;
	osName?: string;
	onOsNameChange?: (os: string) => void;
}

const styles = css({
	maxWidth: `cosmos.article.sm`,
	margin: "0 auto",
});

export const ArchivedVersionTable = ({
	items = [],
	overrideDownloadUrl,
	osName: providedOsName,
	onOsNameChange,
}: ArchivedVersionTableProps): React.JSX.Element | null => {
	const messages = useMessages();
	const detectedOsName = useOsName();

	const [osName, setOsName] = useState(providedOsName);
	const [prestine, setPrestine] = useState(true);

	const handleOsChange = useCallback(
		(newOs: string) => {
			if (onOsNameChange) onOsNameChange(newOs);
			setOsName(newOs);
		},
		[onOsNameChange],
	);

	const handleToggleChange = useCallback(
		(key: string, selected: boolean) => {
			const newOs = selected ? key.toLowerCase() : "";
			handleOsChange(newOs);
		},
		[handleOsChange],
	);

	const { flattenedItems, osMap } = useMemo(() => {
		const flattenedItems: ArchivedVersionTableItemProps[] = [];
		const osMap: Record<string, true> = {};
		items.forEach((item) => {
			item.details.forEach((detail) => {
				flattenedItems.push({
					title: item.title,
					desc: item.desc,
					downloadUrl: detail.downloadUrl,
					os: detail.operatingSystem ? messages[`epic.cosmos.os.${detail.operatingSystem.toLowerCase()}`] : "",
				});
				if (detail.operatingSystem) {
					osMap[detail.operatingSystem] = true;
				}
			});
		});
		return { flattenedItems, osMap };
	}, [items, messages]);

	const osToggles = useMemo(() => {
		return Object.keys(osMap).sort().reverse();
	}, [osMap]);

	useEffect(() => {
		if (prestine && detectedOsName) {
			setPrestine(false);
			if (osToggles.includes(detectedOsName)) {
				if (detectedOsName !== osName) handleOsChange(detectedOsName);
			} else if ("windows" !== osName) handleOsChange("windows");
		} else if (typeof providedOsName === "string" && providedOsName !== osName) {
			handleOsChange(providedOsName);
		}
	}, [osName, handleOsChange, providedOsName, prestine, detectedOsName, osToggles]);

	const filteredItems = useMemo(() => {
		if (!osName) return [];
		return flattenedItems.filter((item) => !osName || (osName && item.os && item.os.toLowerCase() === osName));
	}, [flattenedItems, osName]);

	if (!items?.length) {
		return null;
	}

	return (
		<div className={cx("archived-version-table", styles)}>
			<VStack gap="eds.dimension.24" alignItems="initial">
				<CosmosText variant="headingLG" text={messages["epic.cosmos.plugins.archived.versions"]} />
				{osToggles.length ? (
					<HStack>
						{osToggles.map((key) => {
							return (
								<Toggle
									key={key}
									label={messages[`epic.cosmos.os.${key}`]}
									selected={osName === key.toLowerCase()}
									onRemove={() => handleOsChange("")}
									onChange={(selected: boolean) => handleToggleChange(key, selected)}
								/>
							);
						})}
					</HStack>
				) : null}
				<CosmosTable w="full" gridTemplateColumns="3fr 1fr">
					<CosmosTable.Body w="full" className={css({ "--table-border-radius": "radii.eds.borderRadius.24" })}>
						{filteredItems.map((item) => (
							<ArchivedVersionTableItem
								key={`${item.title}-${item.os}`}
								{...item}
								downloadUrl={overrideDownloadUrl || item.downloadUrl}
							/>
						))}
					</CosmosTable.Body>
				</CosmosTable>
			</VStack>
		</div>
	);
};
